/**
 * The display preferences and their validation, with no dependencies.
 *
 * Kept apart from the reactive store so this can be reasoned about — and
 * tested — without pulling in GSAP, the audio graph or a DOM. The store next
 * door is what applies these; this only decides what a valid set of them is.
 */

/**
 * Languages the bundle ships. Adding one here is not enough on its own: the
 * catalogue in `ui/i18n` is typed against Spanish, so a new language fails to
 * compile until every string exists.
 */
export const LANGUAGES = ['es', 'en'] as const;

export type Language = (typeof LANGUAGES)[number];

export interface DisplaySettings {
  /** Scanlines and vignette. Off is a legitimate accessibility choice. */
  crt: boolean;
  /** The in-app half of reduced motion; the OS setting is honoured regardless. */
  reducedMotion: boolean;
  language: Language;
}

export const DEFAULT_DISPLAY_SETTINGS: DisplaySettings = {
  crt: true,
  // Off by default because the OS preference is OR'd in separately — defaulting
  // this on would mean nobody ever sees the animations.
  reducedMotion: false,
  language: 'es',
};

export const DISPLAY_SETTINGS_KEY = '2k48.settings.display';

/**
 * Reject anything the stored blob might contain that this build does not
 * understand.
 *
 * Preferences outlive the build that wrote them: they survive app updates and
 * sit in storage a user can edit. A value written by a future version — or by
 * hand — must not be able to put the app in a state it cannot render, and one
 * bad field must not discard the good ones beside it.
 */
export function normaliseDisplaySettings(value: unknown): DisplaySettings {
  const raw = (value ?? {}) as Partial<Record<keyof DisplaySettings, unknown>>;
  return {
    crt: typeof raw.crt === 'boolean' ? raw.crt : DEFAULT_DISPLAY_SETTINGS.crt,
    reducedMotion:
      typeof raw.reducedMotion === 'boolean'
        ? raw.reducedMotion
        : DEFAULT_DISPLAY_SETTINGS.reducedMotion,
    language: LANGUAGES.includes(raw.language as Language)
      ? (raw.language as Language)
      : DEFAULT_DISPLAY_SETTINGS.language,
  };
}
